import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Table,
  Card,
  Button,
  Input,
  Space,
  Typography,
  message,
  Spin,
  Tag,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  ArrowLeftOutlined,
  SearchOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { TableColumnsType, TablePaginationConfig } from 'antd';

const { Title, Text } = Typography;
const { Search } = Input;

// Declare global constants injected by Vite define
declare const API_BASE_URL: string;

interface ICD10SLP {
  id: string;
  code: string;
  comorbidity_description: string;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface DataResponse {
  items: ICD10SLP[];
  pagination: PaginationInfo;
}

interface VersionInfo {
  id: string;
  version_number: number;
  upload_status: string;
  row_count: number;
  uploaded_at: string;
  activated_at?: string;
}

const ICD10SLPViewer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  
  const [data, setData] = useState<ICD10SLP[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0,
  });
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  
  const versionId = searchParams.get('version');

  useEffect(() => {
    loadVersionInfo();
    loadData();
  }, [pagination.page, searchText, versionId]);

  const loadVersionInfo = async () => {
    try {
      const token = await getAccessTokenSilently();
      const endpoint = versionId 
        ? `${API_BASE_URL}/api/admin/data/icd10-slp/versions/active`
        : `${API_BASE_URL}/api/admin/data/icd10-slp/versions/active`;
      
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setVersionInfo(data);
      }
    } catch (error) {
      console.error('Error loading version info:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
      });
      
      if (searchText) {
        params.append('search', searchText);
      }
      
      if (versionId) {
        params.append('version_id', versionId);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/data/icd10-slp/slp?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load data');
      }

      const result: DataResponse = await response.json();
      setData(result.items);
      setPagination(result.pagination);
    } catch (error: any) {
      console.error('Error loading data:', error);
      message.error('Failed to load ICD-10 SLP data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPagination(prev => ({
      ...prev,
      page: pagination.current || 1,
      page_size: pagination.pageSize || 50,
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();
      
      if (versionId) {
        params.append('version_id', versionId);
      }
      
      // Fetch all data for export (no pagination)
      params.append('page_size', '10000');
      params.append('page', '1');
      
      const response = await fetch(
        `${API_BASE_URL}/api/admin/data/icd10-slp/slp?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      const result: DataResponse = await response.json();
      
      // Convert to CSV
      const csvContent = [
        ['Code', 'Comorbidity Description'].join(','),
        ...result.items.map(item => 
          [
            `"${item.code}"`,
            `"${item.comorbidity_description.replace(/"/g, '""')}"`,
          ].join(',')
        ),
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `icd10_slp_v${versionInfo?.version_number || 'active'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('Export completed successfully');
    } catch (error: any) {
      console.error('Export error:', error);
      message.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const columns: TableColumnsType<ICD10SLP> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      render: (code: string) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: 'Comorbidity Description',
      dataIndex: 'comorbidity_description',
      key: 'comorbidity_description',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/admin/data/icd10-slp')}
            >
              Back to ICD-10 SLP Management
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              ICD-10 SLP Data Viewer
            </Title>
          </Space>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={isExporting}
          >
            Export CSV
          </Button>
        </div>

        {/* Version Info */}
        {versionInfo && (
          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Version"
                  value={`v${versionInfo.version_number}`}
                  prefix={versionInfo.upload_status === 'active' && <CheckCircleOutlined />}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Status"
                  value={versionInfo.upload_status.charAt(0).toUpperCase() + versionInfo.upload_status.slice(1)}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Total Codes"
                  value={versionInfo.row_count}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Uploaded"
                  value={new Date(versionInfo.uploaded_at).toLocaleDateString()}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Search and Table */}
        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Search
              placeholder="Search by code or description..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              style={{ maxWidth: 400 }}
            />
            
            <Table
              columns={columns}
              dataSource={data}
              loading={loading}
              rowKey="id"
              pagination={{
                current: pagination.page,
                pageSize: pagination.page_size,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                pageSizeOptions: ['20', '50', '100', '200'],
              }}
              onChange={handleTableChange}
            />
          </Space>
        </Card>
      </Space>
    </div>
  );
};

export default ICD10SLPViewer; 